
import { useState } from "react";
import { TableCell, TableRow } from "@/components/ui/table";
import { VerificationBadge } from "@/components/profile/VerificationBadge";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { UserActionsDropdown } from "./UserActionsDropdown";
import { PasswordResetDialogs } from "./PasswordResetDialogs";
import { generateSecurePassword } from "@/utils/passwordUtils";

interface Profile {
  id: string;
  username: string | null;
  is_admin: boolean;
  created_at: string;
  verification_status: 'none' | 'pending' | 'verified' | 'rejected';
  has_verified_email: boolean;
  is_creative: boolean;
}

interface UserTableRowProps {
  user: Profile;
  isAdmin: boolean;
  isCurrentUser: boolean;
  isAppOwner: boolean;
  showButton: boolean;
  isUpdatingRole: string | null;
  isDeletingUser: string | null;
  isUpdatingVerification: string | null;
  onRoleUpdate: (userId: string, makeAdmin: boolean) => void;
  onDeleteUser: (userId: string) => void;
  onVerificationUpdate: (userId: string, status: 'none' | 'verified' | 'verified_creative') => void;
  isMobile: boolean;  // Add the isMobile property to the interface
}

export function UserTableRow({
  user,
  isAdmin,
  isCurrentUser,
  isAppOwner,
  showButton,
  isUpdatingRole,
  isDeletingUser,
  isUpdatingVerification,
  onRoleUpdate,
  onDeleteUser,
  onVerificationUpdate,
  isMobile,
}: UserTableRowProps) {
  const navigate = useNavigate();
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  const handleUserClick = () => {
    navigate(`/user/${user.id}`);
  };

  const handleResetPassword = async () => {
    setIsResettingPassword(true);
    try {
      const generatedPassword = generateSecurePassword();
      setNewPassword(generatedPassword);
      
      const { error } = await supabase.functions.invoke('reset-password', {
        body: { 
          userId: user.id,
          password: generatedPassword
        }
      });

      if (error) throw error;
      
      setShowResetDialog(true);
      toast.success("Password reset successful");
    } catch (error: any) {
      toast.error("Failed to reset password: " + error.message);
      setNewPassword("");
    } finally {
      setIsResettingPassword(false);
    }
  };

  const copyPasswordToClipboard = () => {
    navigator.clipboard.writeText(newPassword);
    toast.success("Password copied to clipboard");
  };

  const isLoading = isUpdatingRole === user.id || isDeletingUser === user.id || isUpdatingVerification === user.id;

  return (
    <>
      <TableRow>
        <TableCell>
          <button 
            onClick={handleUserClick}
            className="hover:underline focus:outline-none focus:underline"
          >
            {user.username || 'No username'}
          </button>
          {isAppOwner && (
            <span className="ml-2 text-xs text-muted-foreground">(Owner)</span>
          )}
        </TableCell>
        {!isMobile && <TableCell>
          {new Date(user.created_at).toLocaleDateString()}
        </TableCell>}
        <TableCell className={isMobile ? "px-2" : ""}>
          <VerificationBadge status={user.verification_status} isCreative={user.is_creative} />
        </TableCell>
        <TableCell className={`${isMobile ? "px-2" : ""} text-right`}>
          <UserActionsDropdown
            isAdmin={isAdmin}
            isCurrentUser={isCurrentUser}
            isAppOwner={isAppOwner}
            showButton={showButton}
            isLoading={isLoading}
            isResettingPassword={isResettingPassword}
            isUpdatingRole={isUpdatingRole === user.id}
            isUpdatingVerification={isUpdatingVerification === user.id}
            isDeletingUser={isDeletingUser === user.id}
            verificationStatus={user.verification_status}
            isCreative={user.is_creative}
            onRoleUpdate={() => onRoleUpdate(user.id, !isAdmin)}
            onVerificationUpdate={(status) => onVerificationUpdate(user.id, status)}
            onResetPassword={() => setShowConfirmDialog(true)}
            onDeleteUser={() => onDeleteUser(user.id)}
          />
        </TableCell>
      </TableRow>

      <PasswordResetDialogs
        username={user.username}
        showConfirmDialog={showConfirmDialog}
        showResetDialog={showResetDialog}
        newPassword={newPassword}
        onConfirmDialogChange={setShowConfirmDialog}
        onResetDialogChange={setShowResetDialog}
        onPasswordReset={() => {
          setShowConfirmDialog(false);
          handleResetPassword();
        }}
        onCopyPassword={copyPasswordToClipboard}
        onClose={() => {
          setShowResetDialog(false);
          setNewPassword("");
        }}
      />
    </>
  );
}
